import React from 'react';
import { TableCell, TableRow, useMediaQuery } from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { Enrollment, EnrollmentHistory } from '@spike/membership-model';
import clsx from 'clsx';
import moment, { Moment } from 'moment-timezone';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import Table from 'components/Memberships/Detail/Table';
import { useCommonStyles as useTableStyles } from 'components/Memberships/Detail/Table/CommonStyles';
import { ToolbarItem } from 'components/UI';
import { ActionsPopup } from 'components/UI/ActionsPopup/ActionsPopup';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEllipsis } from '@fortawesome/pro-regular-svg-icons';
import EnrollmentStatusChip from 'components/Memberships/Enrollment/EnrollmentStatusChip';
import CancelEnrollmentDialog from 'components/Memberships/Enrollment/CancelEnrollmentDialog';
import EnrollmentCheckoutWrapper from 'components/Memberships/Enrollment/EnrollmentCheckoutWrapper';
import UpdateEnrollmentCardDrawer from 'components/Memberships/Enrollment/UpdateEnrollmentCardDrawer';
import ToolbarButtonWithPopper from 'components/UI/Toolbar/ToolbarButtonWithPopper';
import {
    cancelEnrollmentThunk,
    getEnrollmentThunk,
    MembershipsStatus,
    retryMultiplePaymentsThunk,
    updateEnrollmentEndAtOrPausedAtThunk
} from '@spike/memberships-action';
import { useApiClientWrapper, usePermissions, useTimeZone } from 'hooks';
import useEnrollmentCheckout from 'hooks/useEnrollmentCheckout';
import useHideHubSpotChat from 'hooks/useHideHubSpotChat';
import {
    EnrollmentAction,
    getEnrollmentActions,
    getEnrollmentEndAtPatchValue,
    getEnrollmentResumeEndAtPatchValue
} from 'utils/EnrollmentUtils';
import styles from './ClientMemberships.module.scss';
import ResumeEnrollmentDialog from 'components/Memberships/Enrollment/ResumeEnrollmentDialog';

const membershipTableDateFormat = 'MMM DD, YYYY';

const useDialogStyles = makeStyles((theme: Theme) =>
    createStyles({
        'confirmQuestion': {
            fontSize: '16px',
            color: '#000',
            maxWidth: '445px',
            marginLeft: 'auto',
            marginRight: 'auto',
            marginBottom: 10
        },
        'select': {
            textAlign: 'left',
            paddingLeft: '20px',
            paddingRight: '20px',
            maxWidth: '95%',
            marginLeft: 'auto',
            marginRight: 'auto',
            marginBottom: 5,
            [theme.breakpoints.down('md')]: {
                maxWidth: '100%'
            }
        },
        '@global': {
            '[class*="makeStyles-select"] [class*="makeStyles-labelContainer"]': {
                'paddingLeft': 0,
                'paddingRight': 0,
                'marginLeft': 0,
                'marginRight': 0,
                'marginBottom': 5,
                '& .MuiTypography-body1': {
                    fontWeight: 600,
                    fontSize: '16px'
                }
            }
        }
    })
);

const formatNextBillingDate = (nextBillingDateText?: string): string => {
    if (!nextBillingDateText) return '-';

    const date = moment(nextBillingDateText, 'DD/MM/YYYY', true);

    return date.isValid() ? date.format(membershipTableDateFormat) : nextBillingDateText;
};

interface ClientMembershipsTableProps {
    enrollments: EnrollmentHistory[];
    loading: boolean;
    onRefresh: () => void;
    onEnrollmentClick: (enrollment: EnrollmentHistory) => void;
}

export const ClientMembershipsTable: React.FC<ClientMembershipsTableProps> = ({
    enrollments,
    loading,
    onRefresh,
    onEnrollmentClick
}) => {
    const tableStyles = useTableStyles();
    const dialogStyles = useDialogStyles();
    const dispatch = useDispatch();
    const apiClientWrapper = useApiClientWrapper();
    const timezone = useTimeZone();
    const membershipsStatus = useSelector<RootState, MembershipsStatus>(state => state.memberships.status);
    const enrollmentState = useSelector<RootState, Enrollment | null>(state => state.memberships.enrollmentSelected);
    const [showMobileEnrollmentsDropdown, setShowMobileEnrollmentsDropdown] = React.useState(false);
    const [selectedEnrollmentHistory, setSelectedEnrollmentHistory] = React.useState<EnrollmentHistory | null>(null);
    const [selectedEnrollment, setSelectedEnrollment] = React.useState<Enrollment | null>(null);
    const [pendingAction, setPendingAction] = React.useState<EnrollmentAction | null>(null);
    const [showCancelConfirmDialog, setShowCancelConfirmDialog] = React.useState(false);
    const [showResumeConfirmDialog, setShowResumeConfirmDialog] = React.useState(false);
    const [isUpdatingPauseResume, setIsUpdatingPauseResume] = React.useState(false);
    const [showClientCards, setShowClientCards] = React.useState(false);
    const [loadingCards, setLoadingCards] = React.useState(false);
    const [openPopoverId, setOpenPopoverId] = React.useState<string | null>(null);
    const { checkoutInfo, openCheckout, closeCheckout } = useEnrollmentCheckout();
    const isLg = useMediaQuery((theme: Theme) => theme.breakpoints.up('lg'));
    const permissions = usePermissions();

    useHideHubSpotChat(
        (showMobileEnrollmentsDropdown && !isLg) || (showCancelConfirmDialog && !isLg) || showResumeConfirmDialog
    );

    const pauseEnrollment = (enrollment: Enrollment) => {
        if (
            !permissions.memberships.pauseResume ||
            isUpdatingPauseResume ||
            enrollment.membership.enrollmentType !== 'package'
        )
            return;

        setIsUpdatingPauseResume(true);

        dispatch(
            updateEnrollmentEndAtOrPausedAtThunk(
                apiClientWrapper,
                enrollment.id,
                getEnrollmentEndAtPatchValue(enrollment, timezone),
                new Date().toISOString()
            )
        );
    };

    const resumeEnrollment = (extendEndDate: boolean) => {
        if (
            !permissions.memberships.pauseResume ||
            !selectedEnrollment ||
            isUpdatingPauseResume ||
            selectedEnrollment.membership.enrollmentType !== 'package'
        ) {
            return;
        }

        setIsUpdatingPauseResume(true);

        dispatch(
            updateEnrollmentEndAtOrPausedAtThunk(
                apiClientWrapper,
                selectedEnrollment.id,
                extendEndDate
                    ? getEnrollmentResumeEndAtPatchValue(selectedEnrollment, timezone)
                    : getEnrollmentEndAtPatchValue(selectedEnrollment, timezone),
                null
            )
        );
    };

    React.useEffect(() => {
        switch (membershipsStatus) {
            case MembershipsStatus.GettingEnrollment:
                setLoadingCards(true);
                break;
            case MembershipsStatus.GetEnrollmentSuccess:
                setLoadingCards(false);
                setSelectedEnrollment(enrollmentState);

                if (!enrollmentState || !pendingAction) {
                    break;
                }

                switch (pendingAction) {
                    case 'update_card':
                        setShowClientCards(true);
                        break;
                    case 'cancel':
                        setShowCancelConfirmDialog(true);
                        break;
                    case 'collect_payment':
                        openCheckout(enrollmentState);
                        break;
                    case 'try_again':
                        retryPayment(enrollmentState);
                        break;
                    case 'pause':
                        pauseEnrollment(enrollmentState);
                        break;
                    case 'resume':
                        setShowResumeConfirmDialog(true);
                        break;
                    default:
                        break;
                }

                setPendingAction(null);
                break;
            case MembershipsStatus.GetEnrollmentError:
                setLoadingCards(false);
                setPendingAction(null);
                break;
            case MembershipsStatus.CancelEnrollmentSuccess:
            case MembershipsStatus.RetryPaymentSuccess:
            case MembershipsStatus.RetryMultiplePaymentsSuccess:
                setShowCancelConfirmDialog(false);
                onRefresh();
                break;
            case MembershipsStatus.UpdateEndDateSuccess:
            case MembershipsStatus.UpdateEndDateError:
                if (isUpdatingPauseResume) {
                    setIsUpdatingPauseResume(false);
                    setShowResumeConfirmDialog(false);
                    onRefresh();
                }
                break;
            default:
                break;
        }
    }, [enrollmentState, isUpdatingPauseResume, membershipsStatus, onRefresh, openCheckout, pendingAction]);

    const getDaysDiff = (endDate: string | Moment | null | undefined): number | null => {
        if (endDate == null || !moment(endDate).isValid()) return null;

        const today = moment().tz(timezone).startOf('day');
        const end = moment(endDate).tz(timezone).startOf('day');

        return end.diff(today, 'days');
    };

    const getDaysLeft = (enrollment: EnrollmentHistory) => {
        const endDate = enrollment.endDate;
        const daysDiff = getDaysDiff(endDate);

        if (daysDiff === null) {
            return '-';
        }

        if (daysDiff < 0) {
            return 'Expired';
        }

        if (daysDiff === 0) {
            return 'Expires today';
        }

        return `Expires in ${daysDiff} ${daysDiff === 1 ? 'day' : 'days'}`;
    };

    const retryPayment = (enrollment: Enrollment) => {
        if (!permissions.memberships.managePayments) return;

        let enrollmentIds = enrollment.pastDueEnrollments
            ? enrollment.pastDueEnrollments.map(item => item.id).join(',')
            : enrollment.id.toString();

        if (enrollment.pastDueEnrollments && enrollment.pastDueEnrollments.length > 0) {
            enrollmentIds = `${enrollmentIds},${enrollment.id}`;
        }

        dispatch(retryMultiplePaymentsThunk(apiClientWrapper, enrollmentIds));
    };

    const fetchEnrollmentForAction = (action: EnrollmentAction, enrollment: EnrollmentHistory) => {
        setSelectedEnrollmentHistory(enrollment);
        setPendingAction(action);
        setLoadingCards(true);
        dispatch(getEnrollmentThunk(apiClientWrapper, enrollment.enrollmentId));
    };

    const handleEnrollmentAction = (action: EnrollmentAction, enrollment: EnrollmentHistory) => {
        if (
            (action === 'cancel' && !permissions.memberships.cancel) ||
            (['update_card', 'collect_payment', 'try_again'].includes(action) &&
                !permissions.memberships.managePayments)
        )
            return;

        switch (action) {
            case 'update_card':
            case 'cancel':
            case 'collect_payment':
            case 'try_again':
            case 'pause':
            case 'resume':
                fetchEnrollmentForAction(action, enrollment);
                break;
            default:
                break;
        }
    };

    const handleConfirmCancel = (reason: string) => {
        if (!permissions.memberships.cancel || !selectedEnrollment) return;

        dispatch(cancelEnrollmentThunk(apiClientWrapper, selectedEnrollment.id, new Date().toISOString(), reason));
    };

    const headers = (
        <>
            <TableCell className={clsx(tableStyles.headerCell, styles.membershipCell)}>Membership Name</TableCell>
            <TableCell className={clsx(tableStyles.headerCell, styles.petCell)}>Pet</TableCell>
            <TableCell className={tableStyles.headerCell}>Type</TableCell>
            <TableCell className={tableStyles.headerCell}>Start Date</TableCell>
            <TableCell className={tableStyles.headerCell}>End Date</TableCell>
            <TableCell className={tableStyles.headerCell}>Days Left</TableCell>
            <TableCell className={tableStyles.headerCell}>Next Billing</TableCell>
            <TableCell className={tableStyles.headerCell}>Status</TableCell>
            <TableCell className={clsx(tableStyles.headerCell, tableStyles.rightAlign)}>Total</TableCell>
            <TableCell className={clsx(tableStyles.headerCell, styles.actionsCell)}></TableCell>
        </>
    );

    const rows =
        enrollments.length > 0 ? (
            <>
                {enrollments.map(enrollment => {
                    const endDate = enrollment.endDate;
                    const daysDiff = getDaysDiff(endDate);
                    const isDaysLeftWarning = daysDiff !== null && daysDiff >= 0 && daysDiff <= 10;
                    const enrollmentActions = getEnrollmentActions(
                        enrollment.status,
                        enrollment.enrollmentType,
                        permissions
                    );
                    const showActions =
                        enrollment.status !== 'canceled' &&
                        enrollment.status !== 'ended' &&
                        enrollmentActions.length > 0;

                    return (
                        <TableRow
                            key={enrollment.enrollmentId}
                            hover
                            className={clsx(styles.tableRow, {
                                [styles.pausedRow]: enrollment.status === 'paused'
                            })}
                            onClick={() => onEnrollmentClick(enrollment)}
                        >
                            <TableCell className={clsx(tableStyles.cell, styles.membershipNameValue)}>
                                {enrollment.membershipName}
                            </TableCell>
                            <TableCell className={clsx(tableStyles.cell, styles.petValue)}>
                                {enrollment.pets.length > 1
                                    ? enrollment.pets.map(pet => pet.name).join(', ')
                                    : enrollment.pets[0]?.name}
                            </TableCell>
                            <TableCell className={tableStyles.cell}>
                                {enrollment.enrollmentType === 'package' ? 'Package' : 'Subscription'}
                            </TableCell>
                            <TableCell className={tableStyles.cell}>
                                {moment(enrollment.startDate).tz(timezone).format(membershipTableDateFormat)}
                            </TableCell>
                            <TableCell className={tableStyles.cell}>
                                {moment(endDate).isValid()
                                    ? moment(endDate).tz(timezone).format(membershipTableDateFormat)
                                    : enrollment.endsText || '-'}
                            </TableCell>
                            <TableCell
                                className={clsx(tableStyles.cell, {
                                    [styles.daysLeftWarning]: isDaysLeftWarning
                                })}
                            >
                                <span className={clsx({ [styles.daysLeftWarningText]: isDaysLeftWarning })}>
                                    {getDaysLeft(enrollment)}
                                </span>
                            </TableCell>
                            <TableCell className={tableStyles.cell}>
                                {enrollment.nextBillingDateText && enrollment.enrollmentType !== 'package'
                                    ? formatNextBillingDate(enrollment.nextBillingDateText)
                                    : '-'}
                            </TableCell>
                            <TableCell className={tableStyles.cell}>
                                <EnrollmentStatusChip status={enrollment.status} />
                            </TableCell>
                            <TableCell className={clsx(tableStyles.cell, tableStyles.rightAlign)}>
                                ${Number(enrollment.totalAmount).toFixed(2)}
                            </TableCell>
                            <TableCell
                                className={clsx(tableStyles.cell, styles.actionsValue)}
                                onClick={event => event.stopPropagation()}
                            >
                                {isLg && showActions && (
                                    <ToolbarButtonWithPopper
                                        id={`client_enrollment_${enrollment.enrollmentId}`}
                                        isOpen={openPopoverId === `client_enrollment_${enrollment.enrollmentId}`}
                                        onToggle={() => {
                                            setOpenPopoverId(prev =>
                                                prev === `client_enrollment_${enrollment.enrollmentId}`
                                                    ? null
                                                    : `client_enrollment_${enrollment.enrollmentId}`
                                            );
                                        }}
                                        onClose={() => setOpenPopoverId(null)}
                                    >
                                        {getEnrollmentActions(
                                            enrollment.status,
                                            enrollment.enrollmentType,
                                            permissions
                                        ).map((action, index) => (
                                            <ToolbarItem
                                                key={index}
                                                text={action.label}
                                                icon={action.icon}
                                                onClick={event => {
                                                    event.stopPropagation();
                                                    handleEnrollmentAction(action.action, enrollment);
                                                    setOpenPopoverId(null);
                                                }}
                                            />
                                        ))}
                                    </ToolbarButtonWithPopper>
                                )}

                                {!isLg && showActions && (
                                    <button
                                        type="button"
                                        className={styles.actionsButton}
                                        aria-label="Membership actions"
                                        onClick={e => {
                                            e.stopPropagation();
                                            setSelectedEnrollmentHistory(enrollment);
                                            setShowMobileEnrollmentsDropdown(true);
                                        }}
                                    >
                                        <FontAwesomeIcon icon={faEllipsis} />
                                    </button>
                                )}
                            </TableCell>
                        </TableRow>
                    );
                })}
            </>
        ) : (
            <TableRow>
                <TableCell
                    colSpan={10}
                    className={styles.emptyTableCell}
                >
                    <div className={styles.emptyTableState}>
                        <img
                            className={styles.emptyTableImage}
                            src="/images/memberships/clients-notfound.svg"
                            alt="No memberships"
                        />
                        <div className={styles.emptyTableTitle}>No memberships yet</div>
                    </div>
                </TableCell>
            </TableRow>
        );

    return (
        <>
            <Table
                headers={headers}
                rows={rows}
                loading={loading}
                withoutColor={true}
                wrapperClassName={styles.tableWrapper}
            />

            {permissions.memberships.cancel && (
                <CancelEnrollmentDialog
                    open={showCancelConfirmDialog}
                    enrollment={selectedEnrollment}
                    onCancel={() => setShowCancelConfirmDialog(false)}
                    onConfirm={handleConfirmCancel}
                    questionClassName={dialogStyles.confirmQuestion}
                    subquestionClassName={dialogStyles.select}
                />
            )}

            {permissions.memberships.pauseResume && (
                <ResumeEnrollmentDialog
                    open={showResumeConfirmDialog}
                    processing={isUpdatingPauseResume}
                    onCancel={() => setShowResumeConfirmDialog(false)}
                    onResume={resumeEnrollment}
                />
            )}

            {showMobileEnrollmentsDropdown && selectedEnrollmentHistory && !isLg && (
                <ActionsPopup
                    actions={getEnrollmentActions(
                        selectedEnrollmentHistory.status,
                        selectedEnrollmentHistory.enrollmentType,
                        permissions
                    ).map(action => ({
                        ...action,
                        onClick: () => {
                            setShowMobileEnrollmentsDropdown(false);
                            handleEnrollmentAction(action.action, selectedEnrollmentHistory);
                        }
                    }))}
                    onClose={() => setShowMobileEnrollmentsDropdown(false)}
                />
            )}

            {permissions.memberships.managePayments && showClientCards && (
                <UpdateEnrollmentCardDrawer
                    onClose={() => setShowClientCards(false)}
                    enrollment={selectedEnrollment}
                    loading={loadingCards}
                    onFinishUpdate={onRefresh}
                />
            )}

            {permissions.memberships.managePayments && (
                <EnrollmentCheckoutWrapper
                    checkoutInfo={checkoutInfo}
                    onClose={closeCheckout}
                    onPaid={() => {
                        closeCheckout();
                        onRefresh();
                    }}
                />
            )}
        </>
    );
};

export default ClientMembershipsTable;
